/**
 * Commandes récentes — mémoire du geste du jour.
 *
 * Sans elle, la palette s'ouvre sur un sommaire ; avec elle, elle s'ouvre sur
 * ce que l'utilisateur fait réellement de ses journées. C'est ce qui fait
 * passer un poste d'accueil de « chercher » à « appuyer deux touches ».
 *
 * RGPD — CE QUI EST STOCKÉ : uniquement des identifiants de commandes
 * statiques (`patient.create`, `/statistiques`). JAMAIS un résultat de
 * recherche : un nom de patient consulté ne doit pas survivre à la session
 * dans le stockage du navigateur. Le filtrage est fait ici, pas chez
 * l'appelant, pour que la règle ne puisse pas être contournée par oubli.
 */

const STORAGE_KEY = "mada-recent-commands";
const MAX_RECENT = 5;

/** Un identifiant est-il sûr à mémoriser ? Les fiches sont exclues par nature. */
function isPersistable(id: string): boolean {
  // Actions du registre (`domaine.verbe`) et routes de navigation (`/chemin`).
  // Les résultats d'entité sont préfixés par leur type (`patient:<uuid>`).
  return !id.includes(":");
}

export function readRecentCommands(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((value): value is string => typeof value === "string")
      .filter(isPersistable)
      .slice(0, MAX_RECENT);
  } catch {
    // Stockage indisponible (navigation privée) ou contenu corrompu : la
    // palette fonctionne sans, ce n'est pas une erreur à remonter.
    return [];
  }
}

/** Remonte `id` en tête des récents. Sans effet sur un identifiant de fiche. */
export function pushRecentCommand(id: string): void {
  if (!isPersistable(id)) return;
  try {
    const next = [id, ...readRecentCommands().filter((x) => x !== id)].slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Idem : l'échec d'écriture ne doit jamais casser l'action en cours.
  }
}

/** Purge — appelée à la déconnexion, comme le reste des traces locales. */
export function clearRecentCommands(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* rien à faire */
  }
}
